'use client';

import { CloudUpload, Download, X, Zap } from 'lucide-react';
import { GetYourPortraitsButton } from '@components/Button/get-your-portraits';
import Image from 'next/image';
import { PicStudioGlyph } from '@components/PicStudioLogo/PicStudioGlyph';

import { IconContainer } from '@components/ui/icon-container';

import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card';
import NumberOne from '@components/Badge/NumberOne';

export default function ComparisonTable() {
  return (
    <div className="w-full max-w-6xl mx-auto pt-12 md:pt-16">
      {/* Header Section */}
      <div className="text-center mb-12">
        <NumberOne className="mb-6" />
        <h1 className="text-3xl md:text-5xl font-bold mb-4">Save time. Save money.</h1>
        <p className="text-lg md:text-xl max-w-3xl mx-auto">
          Get professional headshots in minutes for a fraction of the cost of a traditional photoshoot.
        </p>
      </div>

      {/* Comparison Section */}
      <div className="grid md:grid-cols-2 gap-8 mb-10">
        {/* Your Product Process */}
        <Card className="p-4 pb-8">
          <CardHeader>
            <div className="relative aspect-square overflow-hidden w-24 h-24 bg-accent rounded-3xl mb-10">
              <PicStudioGlyph
                className="w-full h-full object-cover"
                gradientColors={{ start: '#FFFFFF', end: '#FFFFFF' }}
              />
            </div>
            <CardTitle>With PicStudio.Ai</CardTitle>
          </CardHeader>

          <CardContent className="space-y-8">
            <div className="flex flex-row items-start gap-3">
              <CloudUpload className="w-8 h-8 flex-shrink-0 ml-1.5 text-success" />
              <dl>
                <dt className="font-bold text-lg">
                  Upload selfies <span className="text-muted-foreground text-bold">• 5m</span>
                </dt>
                <dd>Upload at least 8 photos of yourself that show your best self.</dd>
              </dl>
            </div>
            <div className="flex flex-row items-start gap-3">
              <Zap className="w-8 h-8 flex-shrink-0 ml-1.5 text-success" />
              <dl>
                <dt className="font-bold text-lg">
                  Let AI generate your portraits <span className="text-muted-foreground text-bold">• 15m</span>
                </dt>
                <dd>Hang tight while we process your images and create your custom portraits.</dd>
              </dl>
            </div>

            <div className="flex flex-row items-start gap-3">
              <Download className="w-8 h-8 flex-shrink-0 ml-1.5 text-success" />
              <dl>
                <dt className="font-bold text-lg">
                  Download and share <span className="text-muted-foreground">• 5m</span>
                </dt>
                <dd>That was easy! Download your keepers and use them on LinkedIn, Instagram, or anywhere else.</dd>
              </dl>
            </div>
          </CardContent>
        </Card>

        {/* Traditional Process */}
        <Card className="p-4 pb-8">
          <CardHeader>
            <div className="relative aspect-square overflow-hidden w-24 h-24 mb-10">
              <Image
                src="https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/ec06f985-dc02-427e-0a6c-a4c0f60db900/public"
                alt="Camera"
                width={1000}
                height={1000}
                className="w-full h-full object-cover"
              />
            </div>
            <CardTitle>Physical photoshoot</CardTitle>
          </CardHeader>

          <CardContent className="space-y-4 text-lg">
            <div className="flex items-center gap-3">
              <IconContainer size="xs" variant="error" borderless>
                <X strokeWidth={4} className="w-2 h-2 sm:w-3 sm:h-3 text-background" />
              </IconContainer>
              <dl>
                <dt>Find a photographer you like</dt>
              </dl>
            </div>
            <div className="flex items-center gap-3">
              <IconContainer size="xs" variant="error" borderless>
                <X strokeWidth={4} className="w-2 h-2 sm:w-3 sm:h-3 text-background" />
              </IconContainer>
              <dl>
                <dt>Contact and wait for reply</dt>
              </dl>
            </div>
            <div className="flex items-center gap-3">
              <IconContainer size="xs" variant="error" borderless>
                <X strokeWidth={4} className="w-2 h-2 sm:w-3 sm:h-3 text-background" />
              </IconContainer>
              <dl>
                <dt>Coordinate / schedule appointment</dt>
              </dl>
            </div>
            <div className="flex items-center gap-3">
              <IconContainer size="xs" variant="error" borderless>
                <X strokeWidth={4} className="w-2 h-2 sm:w-3 sm:h-3 text-background" />
              </IconContainer>
              <dl>
                <dt>Haircut and plan outfits</dt>
              </dl>
            </div>
            <div className="flex items-center gap-3">
              <IconContainer size="xs" variant="error" borderless>
                <X strokeWidth={4} className="w-2 h-2 sm:w-3 sm:h-3 text-background" />
              </IconContainer>
              <dl>
                <dt>Travel to photo studio</dt>
              </dl>
            </div>
            <div className="flex items-center gap-3">
              <IconContainer size="xs" variant="error" borderless>
                <X strokeWidth={4} className="w-2 h-2 sm:w-3 sm:h-3 text-background" />
              </IconContainer>
              <dl>
                <dt>Awkwardly pose for photos</dt>
              </dl>
            </div>
            <div className="flex items-center gap-3">
              <IconContainer size="xs" variant="error" borderless>
                <X strokeWidth={4} className="w-2 h-2 sm:w-3 sm:h-3 text-background" />
              </IconContainer>
              <dl>
                <dt>Wait for edits and delivery</dt>
              </dl>
            </div>
            <div className="flex items-center gap-3">
              <IconContainer size="xs" variant="error" borderless>
                <X strokeWidth={4} className="w-2 h-2 sm:w-3 sm:h-3 text-background" />
              </IconContainer>
              <dl>
                <dt>Upload photos to profiles</dt>
              </dl>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CTA Section */}
      <div className="text-center gap-4 flex flex-col items-center">
        <GetYourPortraitsButton size="xl" source="comparison-table" text="Create your headshots" className="w-fit" />
        <div className="relative w-full">
          <Image
            src="/svgs/ready-in-15-mins.svg"
            alt="Ready in 15 mins"
            width={1000}
            height={1000}
            className="w-40 -ml-16 relative left-[calc(50%-128px)] invert dark:invert-0"
          />
        </div>
      </div>
    </div>
  );
}
